import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EnvironmentService } from 'src/app/services/environment/environment.service';

type ResponderReceiveSmsResponse = {
  statusCode: number;     // 2000 | 2001 | others
  isSuccess: boolean;
  description: string;
  redirectUrl?: string;
};

@Component({
  selector: 'app-share-location',
  templateUrl: './share-location.component.html',
  styleUrls: ['./share-location.component.scss']
})
export class ShareLocationComponent implements OnInit {
  inviteCode = '';
  rt = '';

  isLoading = false;
  infoMessage = '';
  errorMessage = '';
  redirectUrl = '';

  private hasSubmitted = false;
  private locationAllowed = false;

    private getBaseUrl(): string {
    return this.environmentService.getIA9LightningUrl();
  }

  private getKey(): string {
    return this.environmentService.getIA9LightningKey();
  }



  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private environmentService: EnvironmentService,
  ) {}

  ngOnInit(): void {
    this.inviteCode =
      (this.route.snapshot.paramMap.get('inviteCode') || '').trim();

    // Primary: query param ?rt=...
    this.rt =
      (this.route.snapshot.queryParamMap.get('rt') || '').trim();

    // Fallback: hash-based token (#rt=...)
    if (!this.rt) {
      const hash = (window.location.hash || '').replace('#', '');
      const m = /(^|&)rt=([^&]+)/i.exec(hash);
      if (m?.[2]) this.rt = decodeURIComponent(m[2]).trim();
    }

    if (!this.inviteCode) {
      this.errorMessage = 'Invalid SOS link.';
      return;
    }

    if (!this.rt) {
      this.errorMessage = 'Invalid SOS link.';
      return;
    }

    this.beginAckFlow();
  }

  private beginAckFlow(): void {
    if (this.hasSubmitted) return;
    this.hasSubmitted = true;

    this.isLoading = true;
    this.infoMessage = 'Opening SOS location...';
    this.errorMessage = '';

    this.getBrowserLocation()
      .then(pos => {
         console.log('Responder GPS:', pos.lat, pos.lon, 'accuracy:', (pos as any)?.accuracy);
        this.locationAllowed = true;
        this.callResponderReceiveSms(true, pos.lat, pos.lon)})
      .catch(() => {  
     //  No geolocation prompt, no responder location capture
     this.locationAllowed = false;
     this.callResponderReceiveSms(false);
      });

  }

  private getBrowserLocation(): Promise<{ lat: number; lon: number }> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject();
        return;
      }

      
    let settled = false;
    const timeoutId = window.setTimeout(() => {
      if (!settled) {
        settled = true;
        try { navigator.geolocation.clearWatch(watchId); } catch {}
        reject();
      }
    }, 12000);

     const watchId = navigator.geolocation.watchPosition(
      p => {
        const lat = p.coords.latitude;
        const lon = p.coords.longitude;

        // accept only non-zero and decent accuracy if available
        const acc = p.coords.accuracy ?? 999999;
        if (lat === 0 || lon === 0) return;

        // if accuracy is very poor, keep listening briefly
        if (acc > 5000) return; // 5km threshold (tweak if needed)

        if (!settled) {
          settled = true;
          window.clearTimeout(timeoutId);
          navigator.geolocation.clearWatch(watchId);
          resolve({ lat, lon });
        }
      },
      () => {
        if (!settled) {
          settled = true;
          window.clearTimeout(timeoutId);
          try { navigator.geolocation.clearWatch(watchId); } catch {}
          reject();
        }
      },

       {
        enableHighAccuracy: true,
        maximumAge: 0
      }
      // navigator.geolocation.getCurrentPosition(
      //   p => resolve({
      //     lat: p.coords.latitude,
      //     lon: p.coords.longitude
      //   }),
      //   () => reject(),
      //   {
      //     enableHighAccuracy: true,
      //     timeout: 12000,
      //     maximumAge: 0
      //   }
      );
    });
  }

  private callResponderReceiveSms(isLocationAllowed: boolean, lat?: number, lon?: number): void {

    const url =
      `${this.getBaseUrl()}responder/receive-sms/${encodeURIComponent(this.inviteCode)}` +
      `?rt=${encodeURIComponent(this.rt)}` +
      `&code=${this.getKey()}`;

     const payload: any = {
    rt: this.rt,                
    isLocationAllowed
  };

  if (isLocationAllowed && typeof lat === 'number' && typeof lon === 'number') {
    payload.lat = lat;
    payload.lon = lon;
  }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'X-Responder-Token': this.rt // extra safety
      // 'X_CLIENT_ID': '...' // add only if backend requires it
    });

    this.http.post<ResponderReceiveSmsResponse>(url, payload, { headers })
      .subscribe({
        next: (res) => {
          this.isLoading = false;

          if (!res.isSuccess) {
            this.infoMessage = '';
            this.errorMessage = res.description || 'This SOS request is no longer active.';
            return;
          }

          if (this.locationAllowed) {
            this.infoMessage = 'Location shared. The sender has been notified.';
          } else {
            this.infoMessage = 'Acknowledged without location. The sender has been notified.';
          }

          const redirect = (res?.redirectUrl || '').trim();
          if (redirect) {
            window.location.replace(redirect);
            return;
          }
          this.errorMessage = 'SOS confirmed but redirect URL missing.';
        },
        error: () => {
          this.isLoading = false;
          this.infoMessage = '';
          this.errorMessage =
            'Unable to open the SOS location. Please try again.';
        }
      });
  }
}
